import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Flex, Space } from '@/components/base';
import Button from '@/components/Button/Button';
import ScrollView from '@/components/Common/ScrollView';
import Spinner from '@/components/Common/Spinner';
import ToggleRadio from '@/components/Common/ToggleRadio';
import { useModalContext } from '@/components/Modal/Modal';
import Heading from '@/components/Typography/Heading';
import Typo from '@/components/Typography/Typo';
import { helpscoutMailboxesQueryOptions } from '@/entities/(integration)/helpscout';
import useColor from '@/hooks/domain/useColor';
import { modalStyle } from '@/styles/style';

interface Props {
  workspaceAlias?: string;
  mailbox_ids: string[];
  isNew: boolean;
  onSave: (mailbox_ids: string[]) => void;
}

const HelpScoutMailboxModal: React.FC<Props> = ({
  workspaceAlias,
  mailbox_ids,
  onSave,
  isNew,
}) => {
  const { workspace_id: workspaceAliasFromParam } = useParams();
  const resolvedWorkspaceAlias = workspaceAlias ?? workspaceAliasFromParam;
  if (!resolvedWorkspaceAlias) throw new Error('workspace_id is required');
  const color = useColor();
  const { closeModal } = useModalContext();
  const [localBoxes, setLocalBoxes] = useState<string[]>(mailbox_ids);

  const { data: mailboxes, isFetching } = useQuery(
    helpscoutMailboxesQueryOptions(resolvedWorkspaceAlias, {
      enabled: !!resolvedWorkspaceAlias,
    }),
  );

  useEffect(() => {
    if (mailboxes && isNew) {
      setLocalBoxes(mailboxes.map((box) => box.id));
    }
  }, [mailboxes, isNew]);

  const toggleBox = (id: string) => {
    setLocalBoxes((prev) =>
      prev.includes(id) ? prev.filter((mb) => mb !== id) : [...prev, id],
    );
  };

  const handleSave = () => {
    onSave(localBoxes);
    closeModal();
  };

  return (
    <ScrollView style={{ maxHeight: 'calc(100vh - 80px)' }}>
      <Style d="column">
        <Heading type="h2" color={color.gray800}>
          Helpscout Mailboxes
        </Heading>
        <Space h="4px" />
        <Typo type="label2" color={color.gray700}>
          Select mailboxes to import into Syncly
        </Typo>
        <Space h="24px" />
        <Flex d="column" g="12px">
          {isFetching ? (
            <Flex a="center" j="center" style={{ height: 200 }}>
              <Spinner />
            </Flex>
          ) : (
            mailboxes?.map((box) => (
              <Flex key={box.id} a="center" j="space-between">
                <Typo type="body2" color={color.gray800}>
                  {box.name}
                </Typo>
                <ToggleRadio
                  status={localBoxes.includes(box.id)}
                  onChange={() => toggleBox(box.id)}
                />
              </Flex>
            ))
          )}
          {!mailboxes ||
            (mailboxes.length === 0 && (
              <Flex a="center" j="center" style={{ height: 200 }}>
                <Typo type="body2" color={color.gray600}>
                  No mailboxes found
                </Typo>
              </Flex>
            ))}
        </Flex>
        <Space h="24px" />
        <Flex j="flex-end" g="12px">
          <Button variant="outlined" onClick={closeModal} sizeType="m">
            Cancel
          </Button>
          <Button onClick={handleSave} sizeType="m">
            Save
          </Button>
        </Flex>
      </Style>
    </ScrollView>
  );
};

export default HelpScoutMailboxModal;

const Style = styled(Flex)`
  padding: 24px;
  width: 600px;
  ${modalStyle};
`;
